import {parseContentItem, getShareLink} from 'utils/mediafire';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {FilesItemState} from 'store/slices/files/types';

import type {Store, Actions} from 'store/slices/files/types';

export function move(state: Store, action: Actions['Move']) {
  const {ids} = action.payload;
  state.selection = [];
  state.search = undefined;
  state.query = undefined;
  ids.forEach(id => {
    const item = state.items[id];
    if (item) item.state = FilesItemState.isMoving;
  });
}

export function copy(state: Store, action: Actions['Copy']) {
  const {ids} = action.payload;
  state.selection = [];
  state.search = undefined;
  state.query = undefined;
  ids.forEach(id => {
    const item = state.items[id];
    if (item) item.state = FilesItemState.isCopying;
  });
}

export function update(state: Store, action: Actions['Update']) {
  const {id} = action.payload;
  const item = state.items[id];
  item.state = FilesItemState.isUpdating;
}

export function drag(state: Store, action: Actions['Drag']) {
  const {ids} = action.payload;
  state.drag = ids || undefined;
}

export function select(state: Store, action: Actions['Select']) {
  const {ids, noToggle, focused, view} = action.payload;
  // Range selection
  if (focused && view && ids.length === 1) {
    const list = [...view.folders, ...view.files];
    const to = list.indexOf(ids[0]);
    const from = list.indexOf(focused);
    state.selection = from <= to
      ? list.slice(from, to + 1)
      : list.slice(to, from + 1);
  // Clear selection
  } else if (ids.length === 0) {
    state.selection = [];
  // Set selection
  } else if (noToggle) {
    state.selection = ids;
  // Toggle selection
  } else {
    ids.forEach(id => {
      const index = state.selection.indexOf(id);
      if (index === -1) {
        state.selection.push(id);
      } else {
        state.selection.splice(index, 1);
      }
    });
  }
}

export function resetFolderView(state: Store) {
  state.selection = [];
  state.search = undefined;
  state.query = undefined;
}

export function search(state: Store, action: Actions['Search']) {
  const {id, query} = action.payload;
  state.query = query || undefined;
  state.search = id || undefined;
  state.views.search = undefined;
}

export function filter(state: Store, action: Actions['Filtering']) {
  state.filter = action.payload;
}

export function sort(state: Store, action: Actions['Sorting']) {
  state.sort = action.payload;
}

export function layout(state: Store) {
  // Cycle through: List -> Grid Small -> Grid Medium -> Grid Large -> List
  switch (state.layout) {
    case FileListItemLayout.ListNormal:
      state.layout = FileListItemLayout.GridSmall;
      break;
    case FileListItemLayout.GridSimple:
      // If somehow we're in GridSimple, go to GridSmall
      state.layout = FileListItemLayout.GridSmall;
      break;
    case FileListItemLayout.GridSmall:
      state.layout = FileListItemLayout.GridMedium;
      break;
    case FileListItemLayout.GridMedium:
      state.layout = FileListItemLayout.GridLarge;
      break;
    case FileListItemLayout.GridLarge:
    default:
      state.layout = FileListItemLayout.ListNormal;
      break;
  }
}

export function setLayout(state: Store, action: Actions['SetLayout']) {
  state.layout = action.payload.layout;
}

// NAVIGATING

export function pick(state: Store, action: Actions['Picking']) {
  state.pick = action.payload;
}

export function preview(state: Store, action: Actions['Preview']) {
  const {id, name} = action.payload;
  state.preview = {id, name};
  state.previewProgress = 0;
}

export function previewJob(state: Store, action: Actions['PreviewJob']) {
  const {jobId} = action.payload;
  state.preview.jobId = jobId;
}

export function previewProgress(state: Store, action: Actions['PreviewProgress']) {
  const {progress} = action.payload;
  state.previewProgress = progress;
}

export function previewClose(state: Store) {
  state.preview = undefined;
  state.previewProgress = 0;
}

export function chunk(state: Store, action: Actions['Chunk']) {
  const {id, type, content} = action.payload;
  const keys = [];
  content.folderContent[type].forEach((f: any) => {
    const key = f.folderkey || f.quickkey;
    const cache = state.items[key];
    const data = parseContentItem(f, state.items[id] && state.items[id].hierarchy, null, cache);
    state.items[key] = data;
    keys.push(key);
  });
  
  state.views[id][type] = Array.from(new Set([
    ...state.views[id][type],
    ...keys,
  ]));
}

export function sync(state: Store, action: Actions['Sync']) {
  const {ids} = action.payload;
  ids.forEach(id => {
    const item = state.items[id];
    if (item)
      item.state = FilesItemState.isNormal;
  });
}

export function load(state: Store, action: Actions['Load']) {
  const {id, root, details, depth} = action.payload;
  const chain = depth && depth.chainFolders;
  const parent = details && parseContentItem(
    details.folderInfo,
    null,
    chain,
    state.items[id],
  );

  const folders = [];
  const files = [];

  if (root) state.root = root;
  if (parent) state.items[id] = parent;

  action.payload.folders && (action.payload.folders as any).forEach((f: any) => {
    const cache = state.items[f.folderkey];
    const data = parseContentItem(f, state.items[id] && state.items[id].hierarchy, null, cache);
    state.items[f.folderkey] = data;
    folders.push(f.folderkey);
  });

  action.payload.files && (action.payload.files as any).forEach((f: any) => {
    const cache = state.items[f.quickkey];
    const data = parseContentItem(f, state.items[id] && state.items[id].hierarchy, null, cache);
    state.items[f.quickkey] = data;
    files.push(f.quickkey);
  });

  state.views[id] = {folders, files};
}

export function loadMulti(state: Store, action: Actions['LoadMulti']) {
  const files = [];
  const folders = [];

  action.payload.items && (action.payload.items as any).forEach((f: any) => {
    const id = f.folderkey || f.quickkey;
    const cache = state.items[id];
    const data = parseContentItem(f, null, null, cache);
    state.items[id] = data;
    if (f.folderkey) {
      folders.push(id);
    } else {
      files.push(id);
    }
  });

  action.payload.folders && (action.payload.folders as any).forEach((f: any) => {
    const cache = state.items[f.folderkey];
    const data = parseContentItem(f, null, null, cache);
    state.items[f.folderkey] = data;
    folders.push(f.folderkey);
  });

  action.payload.files && (action.payload.files as any).forEach((f: any) => {
    const cache = state.items[f.quickkey];
    const data = parseContentItem(f, null, null, cache);
    state.items[f.quickkey] = data;
    files.push(f.quickkey);
  });

  if (action.payload.virtual) {
    state.views[action.payload.virtual] = {folders, files};
  }
}

export function loadTrash(state: Store, action: Actions['LoadTrash']) {
  const {id, revision, folderCount, fileCount} = action.payload;
  const folders = [];
  const files = [];

  state.root = {id: 'myfiles', name: 'My Files'};
  state.items[id] = {
    id,
    revision,
    state: 0,
    name: 'Trash',
    type: 'folder',
    url: getShareLink(id, 'folder'),
    folders: folderCount || 0,
    files: fileCount || 0,
    hierarchy: [],
    private: true,
    created: '',
  };

  action.payload.folders && (action.payload.folders as any).forEach((f: any) => {
    const cache = state.items[f.folderkey];
    const data = parseContentItem(f, null, null, cache);
    state.items[f.folderkey] = data;
    folders.push(f.folderkey);
  });

  action.payload.files && (action.payload.files as any).forEach((f: any) => {
    const cache = state.items[f.quickkey];
    const data = parseContentItem(f, null, null, cache);
    state.items[f.quickkey] = data;
    files.push(f.quickkey);
  });

  state.views[id] = {folders, files};
}
